<template>
    <Dialog v-model:open="opened">
        <DialogTrigger>
            <slot />
        </DialogTrigger>
        <DialogContent>
            <DialogHeader>
                <DialogTitle>
                    {{ $t("integrations.stripe.modal.title") }}</DialogTitle
                >
            </DialogHeader>

            <!-- Integration is incomplete -->
            <div class="my-2" v-if="integration.status !== 'active'">
                <p class="text-iron-700">
                    {{ $t("integrations.stripe.modal.incomplete") }}
                </p>
                <div class="mt-5 flex justify-end">
                    <Button
                        as="a"
                        :href="
                            route('app.stripe-integrations.refresh', {
                                integration: props.integration.id,
                            })
                        "
                    >
                        {{ $t("integrations.stripe.modal.refresh") }}
                    </Button>
                </div>
            </div>

            <!-- Integration is active -->
            <div
                v-auto-animate
                class="mt-4 grid gap-3"
                v-if="integration.status === 'active'"
            >
                <p class="text-sm text-iron-500">
                    {{ $t("integrations.stripe.modal.description") }}
                </p>

                <div class="grid grid-cols-2 gap-4">
                    <div class="grid grid-cols-1 gap-1">
                        <Label>{{ $t("integrations.stripe.modal.crn") }}</Label>
                        <Input v-model="crn"></Input>
                    </div>
                    <div class="grid grid-cols-1 gap-1">
                        <Label>{{
                            $t("integrations.stripe.modal.tax_id")
                        }}</Label>
                        <Input v-model="taxId"></Input>
                    </div>
                </div>

                <div v-if="locale === 'cs'" class="mt-4 grid grid-cols-1 gap-1">
                    <Label>{{
                        $t("integrations.stripe.modal.tax_registration")
                    }}</Label>

                    <Select v-model="taxRegistration">
                        <SelectTrigger>
                            <SelectValue
                                placeholder="Vybrat registraci k dani"
                            />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectGroup>
                                <SelectItem value="non-tax-payer"
                                    >Neplátce DPH</SelectItem
                                >
                                <SelectItem value="tax-payer"
                                    >Plátce DPH</SelectItem
                                >
                            </SelectGroup>
                        </SelectContent>
                    </Select>
                </div>

                <div
                    v-auto-animate
                    v-if="crn || taxId"
                    class="grid grid-cols-1 gap-1"
                >
                    <a
                        href="https://dashboard.stripe.com/tax/registrations"
                        target="_blank"
                        v-if="taxRegistration === 'tax-payer'"
                        class="relative mt-3 inline-flex w-full rounded-md border border-iron-300 bg-white px-5 py-3 text-base text-iron-700 shadow-sm hover:bg-iron-50 focus:outline-none focus:ring-2 focus:ring-z-orange-500 focus:ring-offset-2"
                    >
                        <ExternalLink
                            class="absolute right-3 top-3"
                            :size="16"
                        />
                        <p class="text-left font-medium">
                            {{
                                $t("integrations.stripe.modal.vat_registration")
                            }}
                        </p>
                    </a>
                    <a
                        href="https://dashboard.stripe.com/settings/update/public/support-details"
                        target="_blank"
                        class="relative mt-2 inline-flex w-full rounded-md border border-iron-300 bg-white px-4 py-2 text-base text-iron-700 shadow-sm hover:bg-iron-50 focus:outline-none focus:ring-2 focus:ring-z-orange-500 focus:ring-offset-2"
                    >
                        <ExternalLink
                            class="absolute right-3 top-3"
                            :size="16"
                        />
                        <div>
                            <p class="text-left font-medium">
                                {{
                                    $t(
                                        "integrations.stripe.modal.billing_details.title",
                                    )
                                }}
                            </p>

                            <p class="mt-1 text-sm">
                                {{
                                    $t(
                                        "integrations.stripe.modal.billing_details.description",
                                    )
                                }}
                            </p>
                        </div>
                    </a>
                </div>

                <StripeExampleInvoice
                    v-if="locale === 'cs'"
                    :crn="crn"
                    :tax-id="taxId"
                    :is-tax-payer="taxRegistration === 'tax-payer'"
                    :integration-id="props.integration.id"
                >
                    <button
                        class="relative inline-flex w-full rounded-md border border-iron-300 bg-white px-4 py-2 text-base text-iron-700 shadow-sm hover:bg-iron-50 focus:outline-none focus:ring-2 focus:ring-z-orange-500 focus:ring-offset-2"
                    >
                        <div>
                            <p class="text-left font-medium">Příklad faktury</p>

                            <p class="mt-1 text-sm">
                                Ukázka jak bude vypadat faktura odeslaná vašemu
                                zákazníkovi.
                            </p>
                        </div>
                    </button>
                </StripeExampleInvoice>
            </div>

            <DialogFooter v-if="integration.status === 'active'">
                <!-- <Button
                    v-if="removable"
                    @click="$emit('remove-integration')"
                    variant="destructive"
                    class="flex items-center gap-2"
                >
                    <Trash :size="16" />
                    {{ $t("remove") }}
                </Button> -->

                <Button variant="outline" @click="save" :disabled="loading">
                    <span v-if="!loading"> {{ $t("save") }}</span>
                    <span v-if="loading">{{ $t("saving") }}...</span>
                </Button>
                <Button @click="saveAndClose" :disabled="loading">
                    <span v-if="!loading"> {{ $t("save_and_close") }}</span>
                    <span v-if="loading">{{ $t("saving") }}...</span>
                </Button>
            </DialogFooter>
        </DialogContent>
    </Dialog>
</template>

<script setup lang="ts">
import { onMounted, ref } from "vue";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ExternalLink } from "lucide-vue-next";
import {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import { vAutoAnimate } from "@formkit/auto-animate/vue";
import { router } from "@inertiajs/vue3";
import route from "ziggy-js";
import StripeExampleInvoice from "./StripeExampleInvoice.vue";

import { useI18n } from "vue-i18n";

defineEmits(["remove-integration"]);

const props = defineProps<{
    removable?: boolean;
    integration?: any;
    open?: boolean;
}>();

const crn = ref("");
const taxId = ref("");
const taxRegistration = ref<"tax-payer" | "non-tax-payer">();
const opened = ref(props.open ? props.open : false);

const { locale } = useI18n();

const loading = ref(false);

function updateTaxIds({ close = false }: { close: boolean }) {
    router.post(
        route("app.stripe-integrations.tax-ids.store", {
            integration: props.integration.id,
        }),
        {
            taxId: taxId.value,
            crn: crn.value,
            taxPayer: isTaxPayer(),
        },
        {
            onSuccess: () => {
                if (close) {
                    opened.value = false;
                }

                loading.value = false;
            },
        },
    );
}

function isTaxPayer() {
    if (locale.value === "cs") {
        return taxRegistration.value === "tax-payer";
    }

    return null;
}

function saveAndClose() {
    loading.value = true;
    updateTaxIds({ close: true });
}

function save() {
    loading.value = true;
    updateTaxIds({ close: false });
}

onMounted(async () => {
    if (props.integration) {
        crn.value = props.integration.data.crn;
        taxId.value = props.integration.data?.tax_id?.value;
        taxRegistration.value = props.integration.data.taxPayer
            ? "tax-payer"
            : "non-tax-payer";
    }
});
</script>

<style></style>
